<template>
	<div :class="componentClasses" :disabled="disabled">
		<Label
			class="segmented-control__label"
			:labelText="labelText"
			:forElementId="`${uid}_ul`"
			v-if="labelText"
		/>
		<ul class="segmented-control__radio-list" :id="`${uid}_ul`">
			<li
				class="segmented-control__radio"
				:style="segmentStyle"
				v-for="(option, index) in options"
				:key="`${uid}_${index}`"
				ref="segment"
			>
				<input
					class="segmented-control__input"
					type="radio"
					:name="`segmented-control-${uid}`"
					:value="option.value"
					:id="`${uid}_${index}`"
					v-model="selectedValue"
				/>
				<label
					class="segmented-control__input-label"
					:for="`${uid}_${index}`"
					tabindex="0"
					@keydown.space="selectFocusedOption(option)"
				>
					<span class="segmented-control__label-text">
						{{ option.text }}
					</span>
					<span class="segmented-control__icon" v-if="option.icon">
						<i :class="['icon', 'icon-' + option.icon]" />
					</span>
				</label>
			</li>
		</ul>
		<HelpFeedback
			class="segmented-control__help-feedback"
			:helpText="helpText"
			v-if="helpText"
		/>
		<ErrorFeedback
			class="segmented-control__error-feedback"
			:errorMessages="errorMessages"
			v-if="hasError"
		/>
	</div>
</template>

<script>
import Label from '@/components/forms/label/label';
import ErrorFeedback from '@/components/forms/error-feedback/error-feedback';
import HelpFeedback from '@/components/forms/help-feedback/help-feedback';
import BaseComponentMixins from '@/mixins/base-component';
import componentVariantMixin from '@/mixins/component-variant-mixin';
import variantClasses from '@/components/forms/segmented-control/variant-classes/segmented-control-variant-classes';
import formComponentMixin from '@/mixins/form-component-mixin';

export default {
	name: 'SegmentedControl',
	inheritAttrs: false,
	components: {
		Label,
		ErrorFeedback,
		HelpFeedback
	},
	mixins: [BaseComponentMixins, componentVariantMixin, formComponentMixin],
	created() {
		this.selectedValue = this.value;
		this.variantClasses = variantClasses;
	},
	mounted() {
		const segments = this.$refs.segment || [];
		const maxWidth = segments.reduce((previous, current) => {
			const clientRect = current.getBoundingClientRect();
			return Math.max(previous, clientRect.width);
		}, 0);

		this.segmentsMinWidth = `${maxWidth}px`;
	},
	data() {
		return {
			selectedValue: '',
			segmentsMinWidth: 'auto'
		};
	},
	props: {
		value: {
			type: [Number, String, Boolean],
			default: ''
		},
		/**
		 * Array of objects in format { text: 'text', value: 'value' }
		 */
		options: {
			type: Array,
			default: () => []
		},
		iconBeforeLabel: {
			type: Boolean,
			default: false
		},
		labelText: {
			type: String,
			default: null
		},
		helpText: {
			type: String,
			default: null
		}
	},
	computed: {
		componentClasses() {
			return [
				'segmented-control',
				...this.variantClassList,
				this.hasError && 'has-error',
				this.disabled && 'is-disabled',
				this.iconBeforeLabel && 'has-icon-before'
			];
		},
		segmentStyle() {
			return {
				minWidth: this.segmentsMinWidth
			};
		}
	},
	methods: {
		selectFocusedOption(option) {
			this.selectedValue = option.value;
		}
	},
	watch: {
		selectedValue: {
			handler() {
				this.$emit('input', this.selectedValue);
				this.$emit('change', this.selectedValue);
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * Segmented Control Styles
    * Can use SCSS variables = TRUE
*/

@import '@/scss/5-objects/_form.scss';

.segmented-control {
	display: inline-block;

	@mixin afterStyle {
		content: '';
		width: calc(100% + 2px);
		height: calc(100% + 2px);
		@include horizontal-center-with-transform;
		border: $focus-outline;
		z-index: 10;
	}

	&__radio {
		display: inline-flex;
		align-items: center;
		@include body-text('regular');

		&:not(:first-child) .segmented-control__input-label {
			border-left: none;
		}

		&:first-child .segmented-control__input-label,
		&:first-child .segmented-control__input-label::after {
			border-top-left-radius: $border-radius;
			border-bottom-left-radius: $border-radius;
		}

		&:last-child .segmented-control__input-label,
		&:last-child .segmented-control__input-label::after {
			border-top-right-radius: $border-radius;
			border-bottom-right-radius: $border-radius;
		}

		&:focus-visible,
		&:focus {
			outline: none;
		}
	}

	&__input {
		position: absolute;
		display: none;

		&:focus {
			outline: none;
		}

		&:checked + .segmented-control__input-label {
			position: relative;
			border-color: transparent;
			background: $ui-positive-light;

			&::after {
				@include afterStyle;
			}
		}
	}

	&__icon {
		padding-left: $padding-sm;
		pointer-events: none;
		display: inline-flex;

		.icon {
			transform: scale(1.1);
		}
	}

	&__input-label {
		position: relative;
		@include vertical-horizontal-center-with-flex;
		width: 100%;
		min-width: 66px;
		text-align: center;
		padding: $padding-xs $padding-md;
		background: $white;
		color: $black;
		user-select: none;
		@include textfield;
		border-radius: 0;
		cursor: pointer;

		&:focus-visible,
		&:focus,
		&:hover,
		&:active {
			outline: none;
			background: $ui-positive-light;

			&::after {
				@include afterStyle;
			}
		}
	}

	&.has-icon-before {
		.segmented-control {
			&__input-label {
				flex-direction: row-reverse;
			}

			&__icon {
				padding-left: 0;
				padding-right: $padding-sm;
			}
		}
	}

	&--only-icon {
		.segmented-control {
			&__icon {
				padding: 0;
				.icon {
					transform: scale(1.1);
				}
			}

			&__label-text {
				display: none;
			}
		}

		&.has-icon-before {
			.segmented-control__icon {
				padding: 0;
			}
		}
	}
}
</style>
